import { DateRangeFilter } from '@/components/filters/DateRangeFilter';
import { AffiliateFilter, AttributionCodeFilter, ProgramFilter } from '@/components/filters';
import { DASHBOARD_PRESETS } from '@/components/filters/date-range-presets';
import { FilterGroup, PopoverPlacement } from '@/components/filters/filter-button';
import { FilterPanel } from '@/components/ui';
import { Box, Flex, SimpleGrid, VStack } from '@chakra-ui/react';
import type { ChartDataPoint, ConversionChartDataPoint, StatsMetric } from '@fluenceur/types';
import { type ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DASHBOARD_METRIC_POLARITY } from './dashboard-metric-polarity';
import { DashboardStatCard } from './DashboardStatCard';

interface DashboardMetricsCardsProps {
  attributedRevenue?: StatsMetric;
  totalCommissions?: StatsMetric;
  aov?: StatsMetric;
  conversions?: StatsMetric;
  roiAmount?: StatsMetric;
  cpa?: StatsMetric;
  chartData?: ChartDataPoint[];
  conversionChartData?: ConversionChartDataPoint[];
  isLoading?: boolean;
  /**
   * Optional column beside the stat grid (the to-do card). The row wraps to a
   * stack on narrow screens; nothing is reserved when it is absent.
   */
  aside?: ReactNode;
  /** Shown in the cards' place when the stats cannot be read; the aside stays beside it. */
  statsError?: ReactNode;
}

/**
 * Dashboard KPI grid: 6 stat cards, 3 columns x 2 rows (one column on a phone).
 * Row 1: Revenue, Conversions, ROI (primary outcomes)
 * Row 2: Commissions, AOV, CPA (efficiency metrics)
 * An optional `aside` sits to the right of the grid and drops below it when narrow.
 */
export const DashboardMetricsCards: React.FC<DashboardMetricsCardsProps> = ({
  attributedRevenue,
  totalCommissions,
  aov,
  conversions,
  roiAmount,
  cpa,
  chartData,
  conversionChartData,
  isLoading = false,
  aside,
  statsError,
}) => {
  const { t } = useTranslation();

  const revenueSparkline = useMemo(
    () => chartData?.map((d) => ({ value: d.revenue })),
    [chartData]
  );
  const commissionsSparkline = useMemo(
    () => chartData?.map((d) => ({ value: d.commissions })),
    [chartData]
  );
  const conversionsSparkline = useMemo(
    () => conversionChartData?.map((d) => ({ value: d.conversions })),
    [conversionChartData]
  );

  return (
    <VStack alignItems="stretch" gap={4} width="full">
      <FilterPanel
        title={t('dashboard.stats.title')}
        actionsSlot={
          <FilterGroup>
            <Flex gap={2} align="center" wrap="wrap">
              <ProgramFilter />
              <AffiliateFilter paramName="dashboardAffiliateIds" />
              <AttributionCodeFilter />
              <DateRangeFilter
                positioning={{ placement: PopoverPlacement.BottomEnd }}
                showPresets
                presets={DASHBOARD_PRESETS}
                defaultPreset="30d"
              />
            </Flex>
          </FilterGroup>
        }
      />

      <Flex gap={3} wrap="wrap" align="stretch">
        {statsError ? (
          <Box flex="2 1 600px" minW={0} data-testid="dashboard-stats-error">
            {statsError}
          </Box>
        ) : (
          <SimpleGrid
            columns={{ base: 1, sm: 2, lg: 3 }}
            gap={3}
            flex="2 1 600px"
            minW={0}
            alignSelf="flex-start"
          >
            <DashboardStatCard
              label={t('dashboard.stats.attributedRevenue')}
              metric={attributedRevenue}
              isLoading={isLoading}
              polarity={DASHBOARD_METRIC_POLARITY.attributedRevenue}
              chart={revenueSparkline && { data: revenueSparkline, type: 'area' }}
            />
            <DashboardStatCard
              label={t('dashboard.stats.conversions')}
              metric={conversions}
              isLoading={isLoading}
              polarity={DASHBOARD_METRIC_POLARITY.conversions}
              chart={
                conversionsSparkline && {
                  data: conversionsSparkline,
                  type: 'bar',
                  tooltipLabel: t('dashboard.stats.conversions'),
                }
              }
            />
            <DashboardStatCard
              label={t('dashboard.stats.roiAmount')}
              metric={roiAmount}
              isLoading={isLoading}
              polarity={DASHBOARD_METRIC_POLARITY.roiAmount}
              infoTip={t('dashboard.stats.infoTips.roi')}
            />
            <DashboardStatCard
              label={t('dashboard.stats.totalCommissions')}
              metric={totalCommissions}
              isLoading={isLoading}
              polarity={DASHBOARD_METRIC_POLARITY.totalCommissions}
              chart={commissionsSparkline && { data: commissionsSparkline, type: 'area' }}
            />
            <DashboardStatCard
              label={t('dashboard.stats.aov')}
              metric={aov}
              isLoading={isLoading}
              polarity={DASHBOARD_METRIC_POLARITY.aov}
            />
            <DashboardStatCard
              label={t('dashboard.stats.cpa')}
              metric={cpa}
              isLoading={isLoading}
              polarity={DASHBOARD_METRIC_POLARITY.cpa}
              infoTip={t('dashboard.stats.infoTips.cpa')}
            />
          </SimpleGrid>
        )}

        {aside && (
          <Box flex="1 1 300px" minW={0} display="flex" data-testid="dashboard-stats-aside">
            {aside}
          </Box>
        )}
      </Flex>
    </VStack>
  );
};
